import { Feature, GeoJsonProperties, Polygon } from 'geojson';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import IGeoCoordinate from '../interfaces/IGeoCoordinate';
import MapboxApiService from '../services/mapboxApiService';
import ParcelApiService from '../services/parcelApiService';

export default class DriveTimeStore {
    coordinates: IGeoCoordinate | null = null;
    minutes: DriveTimeMinutes = DriveTimeMinutes._30;
    isLoading: boolean = false;
    isIndustrialScarcityLoadingFor: Record<string, boolean> = {};
    isochroneShapes = new Map<string, Feature<Polygon, GeoJsonProperties>>();
    industrialScarcityPercents = new Map<string, number>();

    constructor(
        private mapboxApiService: MapboxApiService,
        private parcelApiService: ParcelApiService,
    ) {
        makeAutoObservable(this);
    }

    get industrialScarcityPercent(): number | null {
        return this.getIndustrialScarcityPercent(this.coordinates, this.minutes);
    }

    get isIndustrialScarcityLoading(): boolean {
        return this.getIsIndustrialScarcityLoading(this.coordinates, this.minutes);
    }

    get isIndustrialScarcitySupported(): boolean {
        return this.minutes !== DriveTimeMinutes._60;
    }

    setCoordinates(coordinates: IGeoCoordinate) {
        this.coordinates = coordinates;
    }

    setMinutes(minutes: DriveTimeMinutes) {
        this.minutes = minutes;
    }

    setIsLoading(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    setIsIndustrialScarcityLoadingFor(key: string, isLoading: boolean) {
        this.isIndustrialScarcityLoadingFor[key] = isLoading;
    }

    getIndustrialScarcityPercent(coordinates: IGeoCoordinate | null | undefined, minutes: DriveTimeMinutes): number | null {
        return coordinates ? this.industrialScarcityPercents.get(this.driveTimeKey(coordinates, minutes)) ?? null : null;
    }

    getIsIndustrialScarcityLoading(coordinates: IGeoCoordinate | null | undefined, minutes: DriveTimeMinutes): boolean {
        return coordinates ? this.isIndustrialScarcityLoadingFor[this.driveTimeKey(coordinates, minutes)] ?? false : false;
    }

    async fetchIsochroneShape(coordinates: IGeoCoordinate, minutes: DriveTimeMinutes): Promise<Feature<Polygon, GeoJsonProperties>> {
        const key = this.driveTimeKey(coordinates, minutes);
        const cachedShape = this.isochroneShapes.get(key);
        if (cachedShape)
            return cachedShape;

        const shape = await this.mapboxApiService.getIsochrone(coordinates, minutes);
        this.isochroneShapes.set(key, shape);

        return shape;
    }

    async fetchIndustrialScarcityPercent(coordinates: IGeoCoordinate, minutes: DriveTimeMinutes): Promise<void> {
        const key = this.driveTimeKey(coordinates, minutes);
        if (this.industrialScarcityPercents.has(key) || this.isIndustrialScarcityLoadingFor[key])
            return;

        this.setIsIndustrialScarcityLoadingFor(key, true);

        try {
            const shape = await this.fetchIsochroneShape(coordinates, minutes);
            const ratio = await this.parcelApiService.getIndustrialScarcityRatio(shape);
            this.industrialScarcityPercents.set(key, ratio * 100);
        } catch (error) {
            toast.error(`Failed to calculate industrial scarcity: ${error}`);
        } finally {
            this.setIsIndustrialScarcityLoadingFor(key, false);
        }
    }

    clear() {
        this.coordinates = null;
    }

    clearForLogout() {
        this.coordinates = null;
        this.isIndustrialScarcityLoadingFor = {};
        this.isochroneShapes.clear();
        this.industrialScarcityPercents.clear();
    }

    private driveTimeKey(coordinates: IGeoCoordinate, minutes: DriveTimeMinutes): string {
        return `${coordinates.longitude}:${coordinates.latitude}:${minutes}`;
    }
}

export enum DriveTimeMinutes {
    _15 = 15,
    _30 = 30,
    _45 = 45,
    _60 = 60,
}
